import type { ReactNode } from "react"

import { Brand } from "@/components/brand"
import { DemoNotice } from "@/components/demo-notice"
import { cn } from "@/lib/utils"

function Skeleton({ className }: { className?: string }) {
  return (
    <span className={cn("loading-skeleton", className)} aria-hidden="true" />
  )
}

function LoadingStatus({ label }: { label: string }) {
  return (
    <span className="sr-only" role="status" aria-live="polite">
      {label}
    </span>
  )
}

function PublicHeaderPending() {
  return (
    <header className="public-header" aria-hidden="true">
      <div className="site-frame flex h-full items-center justify-between gap-4">
        <span className="inline-flex min-h-11 items-center">
          <Brand compact />
        </span>
        <div className="flex items-center gap-2">
          <Skeleton className="h-10 w-10 sm:w-28" />
          <Skeleton className="h-10 w-24" />
        </div>
      </div>
    </header>
  )
}

function PublicPendingFrame({
  children,
  label,
}: {
  children: ReactNode
  label: string
}) {
  return (
    <>
      <PublicHeaderPending />
      <DemoNotice />
      <main aria-busy="true">
        <LoadingStatus label={label} />
        {children}
      </main>
    </>
  )
}

export function BookingHomePending() {
  return (
    <PublicPendingFrame label="Loading tee times">
      <section className="site-frame pt-10 pb-7 sm:pt-14" aria-hidden="true">
        <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <div className="w-full max-w-[690px]">
            <Skeleton className="h-3 w-32" />
            <Skeleton className="mt-5 h-12 w-[82%] max-w-[520px] sm:h-16" />
            <Skeleton className="mt-2 h-12 w-[64%] max-w-[390px] sm:h-16" />
            <Skeleton className="mt-5 h-4 w-full max-w-[610px]" />
            <Skeleton className="mt-2 h-4 w-[78%] max-w-[470px]" />
          </div>
          <div className="grid w-full gap-3 sm:grid-cols-2 lg:w-[430px]">
            <div>
              <Skeleton className="mb-2 h-3 w-16" />
              <Skeleton className="h-11 w-full" />
            </div>
            <div>
              <Skeleton className="mb-2 h-3 w-10" />
              <Skeleton className="h-11 w-full" />
            </div>
          </div>
        </div>
        <div className="mt-9 grid grid-cols-[44px_1fr_44px] gap-2 sm:grid-cols-[44px_repeat(5,1fr)_44px]">
          {Array.from({ length: 7 }, (_, index) => (
            <Skeleton
              key={index}
              className={cn(
                "h-11",
                index > 1 && index < 6 && "hidden sm:block"
              )}
            />
          ))}
        </div>
      </section>

      <section
        className="border-y border-border bg-[#e9ede7]"
        aria-hidden="true"
      >
        <div className="site-frame grid gap-6 py-8 lg:grid-cols-[minmax(0,1fr)_340px]">
          <div className="ruled-panel min-w-0 overflow-hidden">
            <div className="ruled-panel__head">
              <div className="w-full">
                <Skeleton className="h-3 w-32" />
                <Skeleton className="mt-3 h-4 w-52" />
              </div>
              <Skeleton className="h-10 w-52" />
            </div>
            <PendingRows rows={5} />
          </div>
          <div className="ruled-panel overflow-hidden">
            <div className="ruled-panel__head">
              <Skeleton className="h-3 w-28" />
            </div>
            <div className="space-y-4 p-5">
              <Skeleton className="h-8 w-32" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-[72%]" />
              <Skeleton className="h-12 w-full" />
            </div>
          </div>
        </div>
      </section>
    </PublicPendingFrame>
  )
}

export function PublicPagePending({ rows = 3 }: { rows?: number }) {
  return (
    <PublicPendingFrame label="Loading page">
      <section className="site-frame py-11 sm:py-15" aria-hidden="true">
        <div className="max-w-[700px]">
          <Skeleton className="h-3 w-32" />
          <Skeleton className="mt-5 h-14 w-[78%] max-w-[480px]" />
          <Skeleton className="mt-5 h-4 w-full max-w-[620px]" />
          <Skeleton className="mt-2 h-4 w-[72%] max-w-[450px]" />
        </div>
        <div className="ruled-panel mt-9 overflow-hidden">
          <div className="ruled-panel__head">
            <div className="w-full">
              <Skeleton className="h-3 w-28" />
              <Skeleton className="mt-3 h-4 w-56" />
            </div>
          </div>
          <PendingRows rows={rows} />
        </div>
      </section>
    </PublicPendingFrame>
  )
}

function PendingRows({ rows }: { rows: number }) {
  return (
    <div>
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className="rule-row grid min-h-[82px] grid-cols-[72px_minmax(0,1fr)_88px] items-center gap-4 px-5 py-4"
        >
          <Skeleton className="h-7 w-16" />
          <span className="min-w-0">
            <Skeleton className="h-4 w-[44%] min-w-28" />
            <Skeleton className="mt-3 h-3 w-[72%]" />
          </span>
          <Skeleton className="h-10 w-full" />
        </div>
      ))}
    </div>
  )
}

export function StaffPagePending({
  kind = "table",
}: {
  kind?: "dashboard" | "sheet" | "table" | "course" | "shop"
}) {
  const showMetrics = kind === "dashboard" || kind === "course"

  return (
    <div aria-busy="true">
      <LoadingStatus label="Loading staff workspace" />
      <header className="staff-page-head" aria-hidden="true">
        <div className="w-full max-w-[620px]">
          <Skeleton className="h-3 w-28" />
          <Skeleton className="mt-4 h-12 w-[68%] max-w-[390px]" />
          <Skeleton className="mt-4 h-4 w-[58%] max-w-[330px]" />
        </div>
        <div className="flex w-full max-w-[410px] gap-3">
          <Skeleton className="h-11 flex-1" />
          <Skeleton className="h-11 flex-1" />
        </div>
      </header>

      {showMetrics ? (
        <section className="metric-grid mb-6" aria-hidden="true">
          {Array.from({ length: 4 }, (_, index) => (
            <div className="metric" key={index}>
              <Skeleton className="h-3 w-20" />
              <Skeleton className="mt-3 h-9 w-24" />
            </div>
          ))}
        </section>
      ) : null}

      {kind === "sheet" ? (
        <section
          className="ruled-panel mb-6 overflow-hidden"
          aria-hidden="true"
        >
          <div className="ruled-panel__head">
            <div className="w-full">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="mt-3 h-3 w-[52%]" />
            </div>
            <Skeleton className="h-10 w-32" />
          </div>
          <div className="grid gap-4 p-5 sm:grid-cols-3">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        </section>
      ) : null}

      {kind === "shop" ? (
        <>
          <div className="pro-shop-workspace" aria-hidden="true">
            <section className="ruled-panel overflow-hidden">
              <div className="ruled-panel__head">
                <div className="w-full">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="mt-3 h-4 w-[65%]" />
                </div>
              </div>
              <PendingRows rows={3} />
            </section>
            <section className="ruled-panel overflow-hidden">
              <div className="ruled-panel__head">
                <div className="w-full">
                  <Skeleton className="h-3 w-28" />
                  <Skeleton className="mt-3 h-4 w-[52%]" />
                </div>
              </div>
              <PendingRows rows={5} />
            </section>
            <aside className="pro-shop-receipt ruled-panel overflow-hidden">
              <div className="ruled-panel__head">
                <Skeleton className="h-3 w-24" />
              </div>
              <div className="space-y-4 p-5">
                <Skeleton className="h-7 w-[60%]" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-[78%]" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-11 w-full" />
              </div>
            </aside>
          </div>
          <section
            className="ruled-panel mt-6 overflow-hidden"
            aria-hidden="true"
          >
            <div className="ruled-panel__head">
              <div className="w-full">
                <Skeleton className="h-3 w-28" />
                <Skeleton className="mt-3 h-4 w-[35%]" />
              </div>
            </div>
            <PendingRows rows={3} />
          </section>
        </>
      ) : (
        <section className="ruled-panel overflow-hidden" aria-hidden="true">
          <div className="ruled-panel__head">
            <div className="w-full">
              <Skeleton className="h-3 w-32" />
              <Skeleton className="mt-3 h-4 w-[36%] min-w-40" />
            </div>
          </div>
          <PendingRows rows={kind === "course" ? 4 : 6} />
        </section>
      )}
    </div>
  )
}

export function StaffShellPending() {
  return (
    <div className="staff-app">
      <aside className="staff-sidebar" aria-hidden="true">
        <div className="staff-sidebar__brand">
          <Brand />
        </div>
        <div className="staff-course-tag">
          <Skeleton className="h-4 w-4" />
          <span className="w-full">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="mt-2 h-3 w-32" />
          </span>
        </div>
        <div className="space-y-2 px-3 py-4">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-12 w-full" />
          ))}
        </div>
        <div className="staff-sidebar__footer">
          <Skeleton className="size-10 rounded-full" />
          <span className="flex-1">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="mt-2 h-3 w-16" />
          </span>
        </div>
      </aside>
      <div className="staff-main">
        <div className="staff-mobile-bar" aria-hidden="true">
          <div className="staff-mobile-top">
            <Brand compact />
            <Skeleton className="size-9 rounded-full" />
          </div>
          <div className="grid grid-cols-6 gap-1">
            {Array.from({ length: 6 }, (_, index) => (
              <Skeleton key={index} className="h-12 w-full" />
            ))}
          </div>
        </div>
        <DemoNotice compact />
        <main className="staff-content">
          <StaffPagePending kind="dashboard" />
        </main>
      </div>
    </div>
  )
}
